import * as PopoverPrimitive from '@radix-ui/react-popover';
import dayjs from 'dayjs';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib';

dayjs.locale('es');

interface DatePickerProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  label?: string;
  description?: string;
  errorMessage?: string;
  minDate?: Date;
  maxDate?: Date;
}

const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(
  ({ className, value, onChange, placeholder = 'DD/MM/YYYY', disabled, error, label, description, errorMessage, minDate, maxDate, ...props }, ref) => {
    const [open, setOpen] = React.useState(false);
    const [selectedMonth, setSelectedMonth] = React.useState(() => value ? dayjs(value) : dayjs());
    const [displayValue, setDisplayValue] = React.useState(() => value ? formatDate(value) : '');

    const formatDate = (date: Date) => dayjs(date).format('DD/MM/YYYY');
    const parseDate = (str: string): Date | undefined => {
      const parsed = dayjs(str, 'DD/MM/YYYY', true);
      return parsed.isValid() ? parsed.toDate() : undefined;
    };

    React.useEffect(() => {
      if (value) {
        setDisplayValue(formatDate(value));
      } else {
        setDisplayValue('');
      }
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const parsed = parseDate(e.target.value);
      setDisplayValue(e.target.value);
      onChange?.(parsed);
    };

    const handleBlur = () => {
      const parsed = parseDate(displayValue);
      if (parsed) {
        onChange?.(parsed);
      } else if (displayValue) {
        onChange?.(undefined);
      }
    };

    const handleDayClick = (day: dayjs.Dayjs) => {
      if (minDate && day.isBefore(minDate, 'day')) return;
      if (maxDate && day.isAfter(maxDate, 'day')) return;
      const newDate = day.toDate();
      onChange?.(newDate);
      setDisplayValue(formatDate(newDate));
      setOpen(false);
    };

    const daysInMonth = () => {
      const start = selectedMonth.startOf('month');
      const end = selectedMonth.endOf('month');
      const startWeek = start.startOf('week');
      const endWeek = end.endOf('week');
      
      const days: dayjs.Dayjs[] = [];
      let current = startWeek;
      while (current.isBefore(endWeek) || current.isSame(endWeek)) {
        days.push(current);
        current = current.add(1, 'day');
      }
      return days;
    };

    const prevMonth = () => setSelectedMonth(m => m.subtract(1, 'month'));
    const nextMonth = () => setSelectedMonth(m => m.add(1, 'month'));

    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-foreground mb-1.5">{label}</label>
        )}
        <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
          <PopoverPrimitive.Trigger asChild>
            <input
              ref={ref}
              type="text"
              value={displayValue}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={placeholder}
              disabled={disabled}
              readOnly
              className={cn(
                'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                error && 'border-destructive focus-visible:ring-destructive',
                className
              )}
              aria-invalid={error}
              {...props}
            />
          </PopoverPrimitive.Trigger>
          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content className="w-auto p-0" sideOffset={5} align="start">
              <div className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <button
                    onClick={prevMonth}
                    className="p-1 rounded hover:bg-accent"
                    aria-label="Mes anterior"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="flex-1 text-center font-medium">
                    {selectedMonth.format('MMMM YYYY')}
                  </span>
                  <button
                    onClick={nextMonth}
                    className="p-1 rounded hover:bg-accent"
                    aria-label="Mes siguiente"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid grid-cols-7 gap-0 text-center text-sm">
                  {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map((day) => (
                    <div key={day} className="text-muted-foreground py-1 font-medium">
                      {day}
                    </div>
                  ))}
                  {daysInMonth().map((day) => (
                    <button
                      key={day.toISOString()}
                      onClick={() => handleDayClick(day)}
                      disabled={
                        !day.isSame(selectedMonth, 'month') ||
                        (minDate && day.isBefore(minDate, 'day')) ||
                        (maxDate && day.isAfter(maxDate, 'day'))
                      }
                      className={cn(
                        'h-9 w-9 rounded-full text-sm font-medium transition-colors',
                        day.isSame(value, 'day') && 'bg-primary text-primary-foreground',
                        day.isSame(dayjs(), 'day') && 'font-bold ring-2 ring-primary',
                        !day.isSame(selectedMonth, 'month') && 'text-muted-foreground/50',
                        (minDate && day.isBefore(minDate, 'day')) || (maxDate && day.isAfter(maxDate, 'day'))
                          ? 'text-muted-foreground/30 cursor-not-allowed'
                          : 'hover:bg-accent hover:text-accent-foreground'
                      )}
                    >
                      {day.format('D')}
                    </button>
                  ))}
                </div>
              </div>
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
        {description && !errorMessage && (
          <p className="mt-1.5 text-sm text-muted-foreground">{description}</p>
        )}
        {errorMessage && (
          <p className="mt-1.5 text-sm text-destructive" role="alert">{errorMessage}</p>
        )}
      </div>
    );
  }
);
DatePicker.displayName = 'DatePicker';

export { DatePicker };

function formatDate(date: Date): string {
  return dayjs(date).format('DD/MM/YYYY');
}